import { Button } from "@biteship/atom";
import { useState, useEffect } from "react";
import TicketListView from "./components/ticketListView";
import { ReactComponent as WhatsAppLogo } from "./images/WhatsApp.svg";
import DetailTicketView from "./components/detailTicketView";
import { initialStatusFilter, initialTypeFilter } from "./helpers/constant";
import { getTickets, getTicket, getTicketsCount } from "store/action/ticket";
import { APItoData, singleAPItoData } from "./helpers/formatter";
import { ticketsFilter } from "./helpers/filter";
import { TicketIcon, ChevronRightIcon } from "./images/icons";
import CreateTicket from "./components/createTicket";
import { useDispatch } from "react-redux";
import { fetchUpdatedTicketsCount } from "store/action/ticket";

export default function Tickets() {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("input");
  const [view, setView] = useState("list");
  const [windowSize, setWindowSize] = useState([window.innerWidth, window.innerHeight]);
  const [selectedTicket, setSelectedTicket] = useState({});
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [detailTicketLoading, setDetailTicketLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: initialStatusFilter,
    type: initialTypeFilter,
    createdStartDate: null,
    createdEndDate: null,
  });

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    fetchData(filters, "useEffect");
  }, [filters.search, filters.createdStartDate, filters.createdEndDate]);

  const fetchData = async (filters, from) => {
    try {
      setTicketsLoading(true);

      if (from === "useEffect") {
        // Get status filter count
        const [active, processing, solved] = await getFiltersCount(
          {
            createdStartDate: filters.createdStartDate,
            createdEndDate: filters.createdEndDate,
            status: ["active", "processing", "solved"],
            search: filters.search,
          },
          "status"
        );

        // Get type filter count
        const [shipment, cashOnDelivery, retur, claim] = await getFiltersCount(
          {
            createdStartDate: filters.createdStartDate,
            createdEndDate: filters.createdEndDate,
            type: ["shipment", "cashOnDelivery", "retur", "claim"],
            search: filters.search,
          },
          "type"
        );

        setFilters({
          ...filters,
          status: {
            active: {
              ...filters.status.active,
              count: active,
            },
            processing: {
              ...filters.status.processing,
              count: processing,
            },
            solved: {
              ...filters.status.solved,
              count: solved,
            },
          },
          type: {
            shipment: {
              ...filters.type.shipment,
              count: shipment,
            },
            cashOnDelivery: {
              ...filters.type.cashOnDelivery,
              count: cashOnDelivery,
            },
            retur: {
              ...filters.type.retur,
              count: retur,
            },
            claim: {
              ...filters.type.claim,
              count: claim,
            },
          },
        });
      }

      await getAllTickets(filters);

      setTicketsLoading(false);
    } catch (error) {
      setTicketsLoading(false);
    }
  };

  const getFiltersCount = (filters, key) => {
    return Promise.all(
      filters[key].map(async (i) => {
        try {
          const result = await getTicketsCount({ ...filters, [key]: [i] });
          if (result.success) return Promise.resolve(result.count);
          else return Promise.resolve(0);
        } catch (error) {
          return Promise.resolve(0);
        }
      })
    );
  };

  const onClickTicket = async (id, isReadByUser) => {
    try {
      setView("detail");
      setDetailTicketLoading(true);
      const result = await getTicket(id);
      if (result.success) {
        setSelectedTicketId(id);
        if (!isReadByUser) {
          const newTickets = [...tickets];
          const ticketIdx = newTickets.findIndex((i) => i.id === id);
          newTickets[ticketIdx].isReadByUser = true;
          setTickets(newTickets);
        }
        setSelectedTicket(singleAPItoData(result));
        dispatch(fetchUpdatedTicketsCount());
      }

      setDetailTicketLoading(false);
    } catch {
      setDetailTicketLoading(false);
    }
  };

  const getAllTickets = async (filters) => {
    const formattedFilters = {
      ...filters,
      status: Object.keys(filters.status).filter((i) => filters.status[i].selected),
      type: Object.keys(filters.type).filter((i) => filters.type[i].selected),
    };
    const resTickets = await getTickets(formattedFilters);
    if (resTickets.success) setTickets(APItoData(resTickets.tickets));
    else alert("Gagal mengambil data. Silahkan refresh halaman");
  };

  const onChangeFilters = async (filters) => {
    setTicketsLoading(true);
    setFilters(filters);
    await getAllTickets(filters);
    setTicketsLoading(false);
  };

  return (
    <div className="border rounded text-gray-900">
      {view === "detail" && windowSize[0] < 768 ? (
        <div className="flex items-center space-x-3 p-4">
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => {
              setSelectedTicketId("");
              setView("list");
            }}
          >
            <TicketIcon className="w-4 h-4 flex-shrink-0 text-gray-700" aria-hidden />
            <div className="text-gray-700 font-bold text-xs">Tiket Bantuan</div>
          </div>
          <ChevronRightIcon className="w-5 h-5 flex-shrink-0 text-gray-700" aria-hidden />
          <div className="text-primary font-bold text-xs">Detail Tiket</div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 md:px-7 md:py-5">
          <h5 className="m-0 text-lg md:text-xl">Tiket Bantuan</h5>
          <Button
            onClick={() => {
              window.open(
                "https://api.whatsapp.com/send?phone=6281399045077&text=Hai%20Biteship,%20saya%20ingin%20menanyakan%20perihal%20"
              );
              // trackEvent("Ticket ContactCS");
            }}
            title={
              <div className="font-bold text-gray-900 flex items-center text-xs md:text-sm">
                <WhatsAppLogo
                  className="w-4 h-4 md:w-5 md:h-5 text-gray-900 flex-shrink-0 mr-1.5 md:mr-2"
                  aria-hidden
                />
                Hubungi CS
              </div>
            }
            secondary
            className="justify-center border-neutral-40"
          />
        </div>
      )}
      <div
        className={`flex w-full overflow-hidden`}
        style={{ height: windowSize[1] - (windowSize[0] < 768 ? 130 : 142) + "px" }}
      >
        {(windowSize[0] >= 768 || view === "list") && (
          <TicketListView
            view={view}
            windowWidth={windowSize[0]}
            windowHeight={windowSize[1]}
            tickets={tickets}
            selectedTicket={selectedTicket}
            selectedTicketId={selectedTicketId}
            onClickTicket={onClickTicket}
            filters={filters}
            setFilters={onChangeFilters}
            loading={ticketsLoading}
            fetchData={fetchData}
            setOpenModal={setOpenModal}
          />
        )}

        {(windowSize[0] >= 768 || view === "detail") && (
          <DetailTicketView
            ticket={selectedTicket}
            loading={detailTicketLoading}
            setTicket={setSelectedTicket}
          />
        )}

        <CreateTicket
          openModal={openModal}
          setOpenModal={setOpenModal}
          modalType={modalType}
          setModalType={setModalType}
          onCloseSuccessModal={() => window.location.reload()}
        />
      </div>
    </div>
  );
}
